import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { useCustomerAuth } from "../contexts/CustomerAuthContext";
import { useToggle } from "../contexts/ToggleContext";
import { formatApiErrorDetail } from "../lib/api";

export default function Signup() {
    const { signup } = useCustomerAuth();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const next = params.get("next") || "/";
    const referralsOn = useToggle("referrals");
    const [form, setForm] = useState({ email: "", password: "", name: "", referralCode: params.get("ref") || "", birthday: "" });
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        if (form.password.length < 6) {
            setErr("Password must be at least 6 characters");
            return;
        }
        setBusy(true);
        try {
            const extras = {};
            if (referralsOn && form.referralCode.trim()) extras.referralCode = form.referralCode.trim().toUpperCase();
            if (form.birthday) extras.birthday = form.birthday;
            await signup(form.email.trim().toLowerCase(), form.password, form.name.trim(), extras);
            navigate(next, { replace: true });
        } catch (e2) {
            setErr(formatApiErrorDetail(e2.response?.data?.detail) || "Signup failed");
        } finally {
            setBusy(false);
        }
    };

    const startGoogle = () => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUrl = window.location.origin + "/auth/callback?next=" + encodeURIComponent(next);
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    return (
        <div className="min-h-screen bg-ivory" data-testid="signup-page">
            <Header />
            <main className="max-w-md mx-auto px-6 py-16">
                <span className="label-eyebrow">Create account</span>
                <h1 className="mt-3 font-display text-4xl text-inkDark">Join Sukaanyas</h1>
                <p className="mt-1 text-inkMuted">A boutique built for the modern Indian woman.</p>

                <button
                    type="button"
                    onClick={startGoogle}
                    className="mt-8 w-full flex items-center justify-center gap-3 rounded-xl border border-inkDark/10 bg-white px-4 py-3 hover:border-skyblueDeep transition-colors"
                    data-testid="signup-google-button"
                >
                    Continue with Google
                </button>

                <div className="my-6 flex items-center gap-3 text-xs text-inkMuted">
                    <div className="flex-1 h-px bg-inkDark/10" />
                    or with email
                    <div className="flex-1 h-px bg-inkDark/10" />
                </div>

                <form onSubmit={submit} className="space-y-4" data-testid="signup-form">
                    <label className="block">
                        <span className="label-eyebrow">Name</span>
                        <input
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            className="mt-2 w-full rounded-xl border border-inkDark/10 bg-white px-4 py-3 outline-none focus:border-skyblueDeep"
                            data-testid="signup-name-input"
                        />
                    </label>
                    <label className="block">
                        <span className="label-eyebrow">Email</span>
                        <input
                            type="email"
                            required
                            value={form.email}
                            onChange={(e) => setForm({ ...form, email: e.target.value })}
                            className="mt-2 w-full rounded-xl border border-inkDark/10 bg-white px-4 py-3 outline-none focus:border-skyblueDeep"
                            data-testid="signup-email-input"
                        />
                    </label>
                    <label className="block">
                        <span className="label-eyebrow">Password</span>
                        <input
                            type="password"
                            required
                            minLength={6}
                            value={form.password}
                            onChange={(e) => setForm({ ...form, password: e.target.value })}
                            className="mt-2 w-full rounded-xl border border-inkDark/10 bg-white px-4 py-3 outline-none focus:border-skyblueDeep"
                            data-testid="signup-password-input"
                        />
                    </label>
                    {err ? <div className="rounded-xl bg-red-50 text-red-600 px-4 py-3 text-sm" data-testid="signup-error">{err}</div> : null}
                    {referralsOn ? (
                        <label className="block">
                            <span className="label-eyebrow">Referral code (optional)</span>
                            <input
                                value={form.referralCode}
                                onChange={(e) => setForm({ ...form, referralCode: e.target.value })}
                                className="mt-2 w-full rounded-xl border border-inkDark/10 bg-white px-4 py-3 outline-none focus:border-skyblueDeep uppercase tracking-widest"
                                placeholder="e.g. AB12CD34"
                                data-testid="signup-referral-input"
                            />
                        </label>
                    ) : null}
                    <label className="block">
                        <span className="label-eyebrow">Birthday (optional)</span>
                        <input
                            type="date"
                            value={form.birthday}
                            onChange={(e) => setForm({ ...form, birthday: e.target.value })}
                            className="mt-2 w-full rounded-xl border border-inkDark/10 bg-white px-4 py-3 outline-none focus:border-skyblueDeep"
                            data-testid="signup-birthday-input"
                        />
                        <span className="mt-1 block text-xs text-inkMuted">We'll surprise you with a boutique gift in your birthday month.</span>
                    </label>
                    <button type="submit" disabled={busy} className="btn-pill w-full justify-center disabled:opacity-50" data-testid="signup-submit">
                        {busy ? "Creating…" : "Create account"}
                    </button>
                </form>

                <div className="mt-6 text-sm text-inkMuted">
                    Already have an account?{" "}
                    <Link to={`/login?next=${encodeURIComponent(next)}`} className="text-skyblueDeep hover:underline" data-testid="signup-login-link">
                        Sign in
                    </Link>
                </div>
            </main>
            <Footer />
        </div>
    );
}
